import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ImagePlus, X } from 'lucide-react';
import { formatBytes } from '@/lib/intakeUpload';

/**
 * AvatarUpload — single image picker for the artist intake form.
 * Mirrors the OpenCallPage dark / cyan visual language.
 *
 * Props:
 *   value:        File | null
 *   existingUrl:  optional remote PFP URL to show before user picks a new one
 *   onChange(file)
 */
export default function AvatarUpload({ value, existingUrl, onChange }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const setFile = (file) => {
    if (!file) {
      setPreviewUrl(null);
      onChange?.(null);
      return;
    }
    if (!file.type?.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onChange?.(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const showRemote = !value && !previewUrl && existingUrl;
  const showLocal = !!previewUrl;

  return (
    <div
      className="border border-[#222] bg-[#0a0a0a] p-4 sm:p-5 flex items-center gap-4 sm:gap-5"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      data-testid="avatar-upload"
    >
      <div
        className="w-20 h-20 sm:w-24 sm:h-24 border border-[#333] bg-[#050505] shrink-0 overflow-hidden flex items-center justify-center"
        onClick={() => inputRef.current?.click()}
        style={{ cursor: 'pointer' }}
      >
        {showLocal && (
          <img src={previewUrl} alt="profile preview" className="w-full h-full object-cover" />
        )}
        {!showLocal && showRemote && (
          <img src={existingUrl} alt="current profile" className="w-full h-full object-cover" />
        )}
        {!showLocal && !showRemote && (
          <ImagePlus className="w-6 h-6 text-[#444]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-outfit text-base font-semibold text-white">
          Profile Image
        </p>
        <p className="text-[11px] font-mono text-[#888] mt-0.5">
          {value
            ? `${value.name} · ${formatBytes(value.size)}`
            : showRemote
              ? 'A profile image is already on file. Upload a new one to replace it.'
              : 'JPG, PNG or WebP. Up to 10MB.'}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <motion.button
            type="button"
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => inputRef.current?.click()}
            data-testid="avatar-pick-btn"
            className="text-[10px] font-mono px-3 py-2 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 uppercase tracking-[0.15em] transition-colors"
            style={{ cursor: 'pointer' }}
          >
            {value || showRemote ? 'Replace Image' : 'Choose Image'}
          </motion.button>
          {value && (
            <motion.button
              type="button"
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setFile(null)}
              data-testid="avatar-remove-btn"
              className="text-[10px] font-mono px-3 py-2 border border-[#333] text-[#888] hover:text-white uppercase tracking-[0.15em] transition-colors flex items-center gap-1.5"
              style={{ cursor: 'pointer' }}
            >
              <X className="w-3 h-3" />
              Remove
            </motion.button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
    </div>
  );
}
